import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { defaultTo, isEmpty, toLower, startCase, isNil, find, cloneDeep } from 'lodash';
import { withApollo, useMutation } from 'react-apollo';
import QModal from '_Components/Modals/QModal/QModal';
import QButton from '_Components/Buttons/QButton/QButton';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import QSelect from '_Components/Fields/QSelect/QSelect';
import { getSelectedThematicsForSettings } from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import { grayShades } from 'styles/abstracts/colors';

const SelectorContainer = styled.div`
  display: flex;
  justify-content: center;
  font-weight: bold;
`;

const ThematicSelectionSelect = styled(QSelect)`
  margin-right: 2rem;
`;

const ReportExportMention = styled.div`
  color: ${grayShades.g800};
  font-weight: bold;
  font-size: 13px;
`;

const ExportReportModal = ({
  isOpen,
  toggle,
  client,
  projectId,
  projectName,
  setMessage,
  query,
  additionalVariables,
  t,
  i18n,
  analysisAxis,
  selectionList,
  selectedThematicSelectionOption,
  dispatchSelectThematicSelection,
  templateList,
  selectedTemplate,
  dispatchSelectTemplate,
  lang,
  exportPresentation,
}) => {
  const analysisDepths = [
    // {
    //   id: 'thematic',
    //   value: 'thematic',
    //   label: t('filters:Thematics'),
    // },
    {
      id: 'sub_thematic',
      value: 'sub_thematic',
      label: t('filters:Sub thematic'),
    },
    {
      id: 'sub_sub_thematic',
      value: 'sub_sub_thematic',
      label: t('filters:Sub sub thematic'),
    },
  ];

  const CLUSTERS_LABELS = {
    ENCHANTEMENT: t('button:Enchantment'),
    EFFET_WOW: t('button:WOW effect'),
    STORIES: t('button:Stories'),
    IRRITATION: t('button:Irritation'),
    ATTRITION: t('button:Churn'),
  };

  const SENTIMENTS_LABELS = {
    POSITIVE: t('card:Positive'),
    NEUTRAL: t('card:Neutral'),
    NEGATIVE: t('card:Negative'),
  };

  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };

  const noTemplate = {
    id: null,
    label: t('button:No template'),
    name: t('button:No template'),
    isDisabled: true,
  };

  const getFullDate = (date, period) => {
    const dateFormat = moment(period[date]);
    dateFormat.locale(i18n.language);
    return dateFormat.format('LL');
  };

  const getThematicLabel = thematicGroup => {
    const { thematic, subThematic, subSubThematic } = thematicGroup ?? {};
    return find([subSubThematic, subThematic, thematic], item => !isNil(item) && item !== '');
  };

  const criteriaKeyLabel = ckGroup => {
    const { name, values } = ckGroup;
    return `${name}: ${values.join(', ')}`;
  };

  const [currentItem, setCurrentItem] = useState(
    selectionList?.find(option => option.id === selectedThematicSelectionOption?.id) || allThematics,
  );
  const [settings, setSettings] = useState({});
  const [currentAnalysisDepth, setCurrentAnalysisDepth] = useState(analysisDepths[0]);
  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION);
  const [currentTemplate, setCurrentTemplate] = useState(
    templateList?.find(template => template.id === selectedTemplate?.id) || noTemplate,
  );
  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    setCurrentItem(selectThematicSelectionOption);
    const cloneSettings = cloneDeep(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
    });
    setSettings(cloneSettings);
  };

  const onTemplateChange = selectTemplateOption => {
    dispatchSelectTemplate(selectTemplateOption);
    setCurrentTemplate(selectTemplateOption);
  };

  const onAnalysisDepthChange = analysisDepthOption => {
    setCurrentAnalysisDepth(analysisDepthOption);
  };

  const getFilters = () => {
    const {
      range,
      thematics,
      criteriaKeys,
      selectedEmotions,
      keywords,
      selectedClusters,
      selectedSentiments,
    } = defaultTo(additionalVariables, {});

    let startFilterDate;
    let endFilterDate;
    if (range) {
      startFilterDate = getFullDate('startDate', range);
      endFilterDate = getFullDate('endDate', range);
    }

    return (
      <ul>
        {projectName && (
          <li key={`modal-${projectName}`}>
            {t('card:Project')} : {projectName}
          </li>
        )}
        {startFilterDate && endFilterDate && (
          <li key="modal-period">
            {t('card:Period')} : {t('card:From')} {startFilterDate} {t('card:to')} {endFilterDate}
          </li>
        )}
        {!isEmpty(thematics) && (
          <li key="modal-thematics">
            {t('card:Thematics')}: {thematics.map(group => getThematicLabel(group)).join(', ')}
          </li>
        )}
        {!isEmpty(criteriaKeys) && (
          <li key="modal-criteria-keys">
            {t('filters:Filters')}:
            <ul>
              {criteriaKeys.map(group => (
                <li key={`modal-criteria-key-${group.name}`}>{criteriaKeyLabel(group)}</li>
              ))}
            </ul>
          </li>
        )}
        {analysisAxis === 'emotions' && !isEmpty(selectedEmotions) && (
          <li key="modal-selected-emotions">
            {t('card:Emotions')}:{' '}
            {selectedEmotions.map(emotion => t(`emotion:${startCase(toLower(emotion))}`)).join(', ')}
          </li>
        )}
        {analysisAxis === 'selections' && !isEmpty(selectedClusters) && (
          <li key="modal-selected-clusters">
            {t('card:Verbatim selection')}: {selectedClusters.map(cluster => CLUSTERS_LABELS[cluster]).join(', ')}
          </li>
        )}
        {analysisAxis === 'sentiments' && !isEmpty(selectedSentiments) && (
          <li key="modal-selected-clusters">
            {t('card:Sentiments')}:{' '}
            {selectedSentiments.map(sentiment => t('card:', SENTIMENTS_LABELS[sentiment])).join(', ')}
          </li>
        )}
        {!isEmpty(keywords) && (
          <li>
            {t('filters:Keywords')} : {keywords.join(', ')}
          </li>
        )}
        {(currentItem?.id === null || currentItem === null) && (
          <li key="modal-thematics">{`${t('card:Limited thematics')}: ${t('button:All thematics')}`}</li>
        )}
        {currentItem?.id !== null && (
          <li key="modal-thematics">{`${t('card:Limited thematics')} : ${currentItem.label}`}</li>
        )}
      </ul>
    );
  };

  const footer = (
    <div style={{ position: 'relative' }}>
      <QButton
        onClick={() => {
          let keywords: string[] = [];
          for (let k = 0; k < additionalVariables?.texts?.length; k++) {
            if (additionalVariables?.texts[k]?.name === 'keywords') {
              keywords = [...keywords, ...additionalVariables?.texts[k]?.values];
            }
            delete additionalVariables?.texts;
          }
          exportPresentation({
            variables: {
              projectId,
              ...(additionalVariables || {}),
              keywords,
              templateId: currentTemplate.id,
              thematicLevel: currentAnalysisDepth.value,
              thematicSelectionId: currentItem.id,
            },
          });
          toggle(!isOpen);
        }}
      >
        {t('button:Export')}
      </QButton>
    </div>
  );

  const updatedFiltersToDisplay = getFilters();

  const [filtersToDisplay, setFiltersToDisplay] = useState(updatedFiltersToDisplay);

  useEffect(() => {
    const newFiltersToDisplay = getFilters();
    setFiltersToDisplay(newFiltersToDisplay);
  }, [additionalVariables, currentItem]);

  useEffect(() => {
    setCurrentItem(selectionList?.find(option => option.id === selectedThematicSelectionOption?.id) || allThematics);
  }, [selectionList]);

  useEffect(() => {
    setCurrentTemplate(selectedTemplate);
  }, [selectedTemplate]);

  const AILogo = (
    <img
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADIElEQVR4nO1YTWtTQRSdIvhRFesXWAuCgpuCCIILa2ptmryZV2MbKmntyn0hNu9OgsVNduq+/ooutCtBLLyZebEgBKTqyv6F6i+wRG7bpM/0tXkfk5DIO3Ah0MzknNt775wZQmLEiOELJSpGOVObGMCcBOk1AJUbnKkaBn4mvQRuymSdfD0sQ4yTXgEwtdosgFP5nvQCwBBpoHL7oAD1p0idFOlWWIYYxyx7kt/vhW38TleVU4mKUXfD+g1cg2t9/UZK3ARTZcFQmZe0OqhVwN6orIWMzVb7Lz1yznOmngJV8xhFo/Ikzz6c0CYAmPoZVgCubbW/ZVTG6+RhLwpU3tEowEmELaFWB1yBysFm8oBhyLnSyOpZ0t1NXOuzDMU8BdAdESOkHcBRiSPTa4wCUxN+9ylMfLpxKHmKYc9Dxr7UFhGYZY/sv/O7PpdbOVagcvpoAWo+SEKCl9OB/4Dz0O96ztRwS/J0N6zk2lBXmbml7JeLnDqzfgWAqbLlTLVfvwDmJILYaSwbzHwg8nQ3OBUzPC2vY+OTzqPW9zzlXOPMeRyUODRFiVUmca+OUS9O2leOHJUhg6dlEk/wthF/kaqe45PygW7i0DRmi6a6vzBmn9FKHo2Z29u0OzgVs3iiaxMAhp3tFHloiPA/wn0IUJlOCwCdjwqlVOUqWuLOCRBTi9P2ANGJcu7HcctwboeZ+eA/6znLFLfwfNFK/h8hmWo/Z+Ku1qY25Rzu+WzMPkk6BctYv8DNz0kdY7MU9p6QZ+oyMPWdU/UNP4cSklwb2rn7Bp0ypjAxCSQKgMo3Lu//OlJZUTEThPxKrhatzq3c+ilgasvlPn9HcYhozPyWjRU180ieM/XW48K+jH8LtyteJ+3WHsmIcK3EOsdScWfeQ8QWfidMTxx6oacN8nPlKD5np2EDvECE+Q2vJxXQ9bSC06bdAhanvw54nRFFHY9b7S6hhggq7h2YPEwNE13Ym0DLept4Hzz98XSBCpflEFPa7UHzGOVM/tJ50caGRksMzEloN2Z1YKm4sv+K9BryaCXwvZPKjSg1HyNGjBj/N/4CHLpRmzINzWUAAAAASUVORK5CYII="
      alt="sparkling--v1"
      style={{ height: '100%', width: '100%' }}
    />
  );
  const modalTitle = t('card:Export standard report');

  const thematicSelectionOptions =
    selectionList.length > 0
      ? [allThematics, ...selectionList].map(selection => {
          return {
            ...selection,
            isDisabled: selection.id === currentItem.id,
            disabled: selection.id === currentItem.id,
          };
        })
      : [{ ...allThematics, isDisabled: currentItem.id === null, disabled: currentItem.id === null }];

  const templateOptions =
    templateList.length > 0
      ? templateList.map(template => {
          return {
            ...template,
            isDisabled: template.id === currentTemplate.id,
            disabled: template.id === currentTemplate.id,
          };
        })
      : [];

  const analysisDepthOptions = analysisDepths.map(analysisDepth => {
    return {
      ...analysisDepth,
      isDisabled: analysisDepth.id === currentAnalysisDepth.id,
      disabled: analysisDepth.id === currentAnalysisDepth.id,
    };
  });

  return (
    <QModal
      isOpen={isOpen}
      title={modalTitle}
      exportReportModal
      logo={AILogo}
      onClose={() => toggle(!isOpen)}
      size="md"
      footer={footer}
      config={{ mentions: false, date: false }}
    >
      <br />
      <b>{t('card:Choose your report')}</b>
      <br />
      <SelectorContainer>
        <ThematicSelectionSelect
          name="reportNameSelect"
          value={currentTemplate}
          searchable={false}
          clearable={false}
          placeholder={t('card:Current report')}
          onChange={onTemplateChange}
          options={templateOptions}
          width={244}
        />
      </SelectorContainer>
      <br />
      <b>{t('card:Choose your thematic selection')}</b>
      <br />
      <SelectorContainer>
        <ThematicSelectionSelect
          name="thematicSelectionSelect"
          value={currentItem}
          searchable={false}
          clearable={false}
          placeholder={t('selections:Current selection')}
          onChange={onThematicSelectionChange}
          options={thematicSelectionOptions}
          width={244}
        />
      </SelectorContainer>
      <br />
      <b>{t('card:Choose analysis depth level')}</b>
      <br />
      <SelectorContainer>
        <QSelect
          name="analysisDepthSelect"
          value={currentAnalysisDepth}
          searchable={false}
          clearable={false}
          placeholder={t('selections:Current analysis depth')}
          onChange={onAnalysisDepthChange}
          options={analysisDepthOptions}
          width={244}
        />
      </SelectorContainer>
      <br />
      <ReportExportMention
        dangerouslySetInnerHTML={{
          __html: t('button:Export report mention'),
        }}
      />
      <br />
      <span>{t('filters:Current selection')}: </span>
      {filtersToDisplay}
      <br />
    </QModal>
  );
};

export default withTranslation()(withApollo(ExportReportModal));
